import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router'
import PropTypes from 'prop-types'
import { ROLES } from 'config/constants'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import { ERROR_TYPE_CONTENTFUL } from 'layers/errorHandling/apiError'
import withApiError from 'layers/errorHandling/apiError/component'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { navigationEvent } from 'layers/navigation/store/operations'
import {
  selectors as licenseSelectors,
  operations as licenseOperations,
} from 'store/licenseManager'
import ssapiSelectors from 'store/ssapiManager/selectors'
import siteOperations from 'store/siteManager/operations'
import { operations as userSessionOperations } from 'store/userSessionManager'
import { TOGGLE_VIEW } from 'store/userSessionManager/constants'
import { TABLE_URL_PARAMS } from 'store/reportsManager/constants'
import AdminDashboardBanner from './component'
import { findProgramLinks } from 'utils/findProgramLinks'
import { Logger } from 'logging'
import { getMSOptInData } from 'utils/siteData'
import { isFlagOn } from 'utils/featureFlags'
import {
  FLAG_CORE_LESSON_COMPLETION_UPDATES,
  FLAG_LEADER_DASHBOARD_REDESIGN,
} from './constants'

export const AdminDashboardBannerContainer = ({
  activeAdminHsSites,
  activeAdminK8Sites,
  adminK8SitesWithDwb,
  claims,
  displayTitle,
  fetchSetupLicensesForUser,
  isFetchingSetupLicenses,
  isLaunched,
  isSetup,
  isSetupAdmin,
  hasDwbAccess,
  hsToggleView,
  isSingleK8Site,
  k8ToggleView,
  mspSites,
  navigationEvent,
  onChangeProgram,
  onboardingSnapshot,
  programQuickLinks,
  programTitles,
  recentLeaderDashboardUrl,
  selectedProgram,
  setHSToggleView,
  setK8ToggleView,
  setLeaderDashboardRecentUrl,
  unassignedLicenses,
  updateMSPOptIn,
}) => {
  const getSitesForProgram = program => {
    if (!program) return []

    switch (program.key) {
      case 'HS':
        return activeAdminHsSites || []
      case 'K8':
        return activeAdminK8Sites || []
      case 'SELA':
      case 'BPU':
      case 'CPU':
        return []
      default:
        return []
    }
  }

  const sitesForSelectedProgram = getSitesForProgram(selectedProgram)
  const sitesForBanner =
    selectedProgram?.key === 'K8' &&
    k8ToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS &&
    adminK8SitesWithDwb?.length > 0
      ? adminK8SitesWithDwb
      : sitesForSelectedProgram

  useEffect(() => {
    if (isSetupAdmin && !isFetchingSetupLicenses && !unassignedLicenses)
      fetchSetupLicensesForUser()
  }, [])

  const location = useLocation()
  const isUserProgressPage = location?.pathname?.includes('/user-progress')
  const isSmallGroupLessonCompletionPage = location?.pathname?.includes(
    '/small-group-lesson-completion',
  )

  const getAvailableLinks = () => {
    if (!programQuickLinks || !selectedProgram) {
      return []
    }

    const {
      hasBpuAdminClaim,
      hasCpuAdminClaim,
      hasElemAdminClaim,
      hasHighschoolAdminClaim,
      hasMsAdminClaim,
      hasSelaAdminClaim,
    } = claims

    const programAccessMap = {
      BPU: { access: hasBpuAdminClaim, program: 'bpuDigital' },
      CPU: { access: hasCpuAdminClaim, program: 'cpuDigital' },
      HS: { access: hasHighschoolAdminClaim, program: 'highSchool' },
      K8: {
        access: hasElemAdminClaim || hasMsAdminClaim,
        k5program: 'k5SelDigital',
        msprogram: 'mspDigital',
      },
      SELA: { access: hasSelaAdminClaim, program: 'sela' },
    }

    return programAccessMap?.[selectedProgram.name]?.access
      ? findProgramLinks(programQuickLinks, selectedProgram, programAccessMap)
      : []
  }

  const handleKeyPress = (e, viewOrUrl) => {
    if (e.key !== 'Enter') return

    const coreLessonOn = isFlagOn([FLAG_CORE_LESSON_COMPLETION_UPDATES])
    const redesignOn = isFlagOn([FLAG_LEADER_DASHBOARD_REDESIGN])
    const firstK8TabView = redesignOn ? TOGGLE_VIEW.SETUP : TOGGLE_VIEW.PREPARE

    const k8LeaderTabViews = [
      firstK8TabView,
      TOGGLE_VIEW.LAUNCH,
      TOGGLE_VIEW.INTERVENTIONS,
      ...(coreLessonOn
        ? [
            TOGGLE_VIEW.CORE_UNITS,
            TOGGLE_VIEW.SPECIALIZED_UNITS,
            TOGGLE_VIEW.MONITOR,
          ]
        : [TOGGLE_VIEW.MONITOR]),
    ]

    const isHsLeaderTabKey =
      selectedProgram?.key === 'HS' &&
      (viewOrUrl === TOGGLE_VIEW.LAUNCH || viewOrUrl === TOGGLE_VIEW.MONITOR)

    if (k8LeaderTabViews.includes(viewOrUrl) || isHsLeaderTabKey) {
      handleTabChange(viewOrUrl)
    } else {
      navigateToSameTab(viewOrUrl)
    }
  }

  const handleTabChange = (view, siteId) => {
    const coreLessonOn = isFlagOn([FLAG_CORE_LESSON_COMPLETION_UPDATES])
    const redesignOn = isFlagOn([FLAG_LEADER_DASHBOARD_REDESIGN])
    const firstK8TabView = redesignOn ? TOGGLE_VIEW.SETUP : TOGGLE_VIEW.PREPARE

    const isLeaderDashboardK8Tab =
      selectedProgram?.key === 'K8' &&
      (view === firstK8TabView ||
        view === TOGGLE_VIEW.LAUNCH ||
        view === TOGGLE_VIEW.INTERVENTIONS ||
        (!coreLessonOn && view === TOGGLE_VIEW.MONITOR) ||
        (coreLessonOn &&
          (view === TOGGLE_VIEW.CORE_UNITS ||
            view === TOGGLE_VIEW.SPECIALIZED_UNITS ||
            view === TOGGLE_VIEW.MONITOR)))

    const isLeaderDashboardHSTab =
      selectedProgram?.key === 'HS' &&
      (view === TOGGLE_VIEW.LAUNCH || view === TOGGLE_VIEW.MONITOR)

    if (isLeaderDashboardK8Tab || isLeaderDashboardHSTab) {
      let pathSegment

      if (selectedProgram.key === 'K8' && coreLessonOn) {
        const views = {
          [TOGGLE_VIEW.LAUNCH]: 'launch',
          [TOGGLE_VIEW.INTERVENTIONS]: 'interventions',
          [TOGGLE_VIEW.CORE_UNITS]: 'core',
          [TOGGLE_VIEW.SPECIALIZED_UNITS]: 'specialized_units',
          [TOGGLE_VIEW.MONITOR]: 'monitor',
          [TOGGLE_VIEW.SETUP]: 'setup',
          [TOGGLE_VIEW.PREPARE]: 'prepare',
        }
        pathSegment = view === TOGGLE_VIEW.MONITOR ? 'core' : views[view]
        let pagedViewUrl = `/admin/program/${selectedProgram.key.toLowerCase()}/`
        setLeaderDashboardRecentUrl(`${pagedViewUrl.slice(1)}${pathSegment}`)
        setK8ToggleView(
          view === TOGGLE_VIEW.MONITOR ? TOGGLE_VIEW.CORE_UNITS : view,
        )
      } else if (selectedProgram.key === 'K8') {
        const views = {
          [TOGGLE_VIEW.PREPARE]: 'prepare',
          [TOGGLE_VIEW.SETUP]: 'setup',
          [TOGGLE_VIEW.LAUNCH]: 'program_adoption',
          [TOGGLE_VIEW.MONITOR]: 'monitor',
          [TOGGLE_VIEW.INTERVENTIONS]: 'interventions',
        }
        pathSegment = views[view]
        let pagedViewUrl = `/admin/program/${selectedProgram.key.toLowerCase()}/`
        setLeaderDashboardRecentUrl(`${pagedViewUrl.slice(1)}${pathSegment}`)
        setK8ToggleView(view)
      } else if (selectedProgram.key === 'HS') {
        const views = {
          [TOGGLE_VIEW.LAUNCH]: 'launch',
          [TOGGLE_VIEW.MONITOR]: 'monitor',
        }
        pathSegment = views[view]
        let pagedViewUrl = `/admin/program/${selectedProgram.key.toLowerCase()}/`
        setLeaderDashboardRecentUrl(`${pagedViewUrl.slice(1)}${pathSegment}`)
        setHSToggleView(view)
      }

      let pagedViewUrl = `/admin/program/${selectedProgram.key.toLowerCase()}/`
      const absoluteUrl = `${pagedViewUrl}${pathSegment}`
      navigationEvent(absoluteUrl, 'REPLACE')

      try {
        pagedViewUrl += pathSegment
        Logger.logEvent({
          name: pagedViewUrl,
          properties: { viewOption: pathSegment },
        })
      } catch (e) {
        console.error('Cannot log Admin toggle event')
      }
    } else {
      let viewOption = `${view}?${TABLE_URL_PARAMS.SITE_ID}=${siteId}`
      navigationEvent(viewOption, 'REPLACE')
    }
  }

  const navigateToSameTab = url => {
    const type = NavigationHelper.types.OPEN
    navigationEvent(url, type)
  }

  return (
    <AdminDashboardBanner
      availableLinks={getAvailableLinks()}
      displayTitle={displayTitle}
      handleKeyPress={handleKeyPress}
      handleTabChange={handleTabChange}
      hasDwbAccess={hasDwbAccess}
      hsToggleView={hsToggleView}
      isLaunched={isLaunched}
      isSetup={isSetup}
      isSingleK8Site={isSingleK8Site}
      isSmallGroupLessonCompletionPage={isSmallGroupLessonCompletionPage}
      isUserProgressPage={isUserProgressPage}
      k8ToggleView={k8ToggleView}
      mspSites={mspSites}
      navigateToSameTab={navigateToSameTab}
      navigationEvent={navigationEvent}
      onChangeProgram={onChangeProgram}
      onboardingSnapshot={onboardingSnapshot}
      programTitles={programTitles}
      recentLeaderDashboardUrl={recentLeaderDashboardUrl}
      selectedProgram={selectedProgram}
      sites={sitesForBanner}
      unassignedLicenses={unassignedLicenses}
      updateMSPOptIn={updateMSPOptIn}
    />
  )
}

AdminDashboardBannerContainer.propTypes = {
  activeAdminHsSites: PropTypes.array,
  activeAdminK8Sites: PropTypes.array,
  adminK8SitesWithDwb: PropTypes.array,
  claims: PropTypes.object,
  displayTitle: PropTypes.string,
  fetchSetupLicensesForUser: PropTypes.func,
  hasDwbAccess: PropTypes.bool,
  hsToggleView: PropTypes.string,
  isFetchingSetupLicenses: PropTypes.bool,
  isLaunched: PropTypes.bool,
  isSetup: PropTypes.bool,
  isSetupAdmin: PropTypes.bool,
  isSingleK8Site: PropTypes.bool,
  k8ToggleView: PropTypes.string,
  mspSites: PropTypes.array,
  navigationEvent: PropTypes.func,
  onChangeProgram: PropTypes.func,
  onboardingSnapshot: PropTypes.array,
  programQuickLinks: PropTypes.array,
  programTitles: PropTypes.array,
  recentLeaderDashboardUrl: PropTypes.string,
  selectedProgram: PropTypes.object,
  setHSToggleView: PropTypes.func,
  setK8ToggleView: PropTypes.func,
  setLeaderDashboardRecentUrl: PropTypes.func,
  siteId: PropTypes.string,
  unassignedLicenses: PropTypes.array,
  updateMSPOptIn: PropTypes.func,
}

const mapStateToProps = state => {
  const {
    reportsManager,
    userAccessManager,
    licenseManager,
    userSessionManager: {
      adminDashboard: {
        hs: { toggleView: hsToggleView },
        k8: { toggleView: k8ToggleView },
        recentLeaderDashboardUrl,
      },
    },
    userProfileManager,
  } = state
  const { selectedProgram, programTitles, programs } = reportsManager || {}
  const { claims } = userAccessManager || {}
  const { unassignedLicenses, isFetchingSetupLicenses } = licenseManager || {}
  const sites = ssapiSelectors.selectUserSites(state)
  const {
    profile: { roles },
  } = userProfileManager || {}
  const isSetupAdmin = roles?.includes(ROLES.SETUP_ADMIN)
  const unassignedSetupLicenses = isSetupAdmin ? unassignedLicenses : []
  const activeAdminHsSites = licenseSelectors.selectActiveAdminHsSites(state)
  const activeAdminK8Sites = licenseSelectors.selectActiveAdminK8Sites(state)
  const adminK8SitesWithDwb = licenseSelectors.selectActiveAdminK8SitesWithDwb(
    state,
  )
  const hasDwbAccess = adminK8SitesWithDwb.length > 1
  const mspSites = getMSOptInData(sites)
  const isSingleK8Site = activeAdminK8Sites?.length === 1

  return {
    activeAdminHsSites,
    activeAdminK8Sites,
    adminK8SitesWithDwb,
    claims,
    hasDwbAccess,
    hsToggleView,
    isFetchingSetupLicenses,
    isSetupAdmin,
    isSingleK8Site,
    k8ToggleView,
    mspSites,
    programs,
    programTitles,
    recentLeaderDashboardUrl,
    selectedProgram,
    unassignedLicenses: unassignedSetupLicenses,
  }
}

const mapDispatchToProps = {
  fetchSetupLicensesForUser: licenseOperations.fetchSetupLicensesForUser,
  navigationEvent,
  setHSToggleView: userSessionOperations.adminSetHSToggleView,
  setK8ToggleView: userSessionOperations.adminSetK8ToggleView,
  setLeaderDashboardRecentUrl:
    userSessionOperations.setLeaderDashboardRecentUrl,
  updateMSPOptIn: siteOperations.updateMSPOptIn,
}

const mapper = entry => {
  const { displayTitle, programQuickLinks } = entry || {}
  return {
    displayTitle,
    programQuickLinks,
  }
}

const options = {
  entryId: '5oqdbnI289AADFVmm2sJyx',
  include: 3,
  mapper,
  spread: true,
}

export default ContentEntryDataGetter(
  withApiError(AdminDashboardBannerContainer, [ERROR_TYPE_CONTENTFUL]),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
